import React from "react";
import { RobotIcon, SmileyIcon, SparkleIcon, SendIcon } from "../Auth/onboarding/icons";
import { isLucideIcon } from "./launcherIcons";
import LauncherGlyph from "./LauncherGlyph";
import { ChatBubbleIcon, MinusIcon } from "../Dashboard/icons";
import { fontStackFor } from "../../utils/fonts";

const launcherIcons = {
  bubble: <ChatBubbleIcon />,
  smile: <SmileyIcon />,
  spark: <SparkleIcon />,
  logo: <RobotIcon />,
};

// Shared with the floating launcher button in PreviewPanel so both honour the
// uploaded avatar and the chosen launcher style.
export const launcherIcon = (settings) => {
  if (settings.avatarImage) return <img src={settings.avatarImage} alt="" />;

  // Icons picked from the library are stored as "lucide:Name".
  if (isLucideIcon(settings.launcherStyle)) {
    return (
      <LauncherGlyph
        value={settings.launcherStyle}
        fallback={<RobotIcon />}
      />
    );
  }

  return launcherIcons[settings.launcherStyle] || <RobotIcon />;
};

const nameWeights = { Regular: 500, Medium: 600, Bold: 700 };
const messageFontScale = { Small: 0.9, Medium: 1, Large: 1.12 };

export default function WidgetPreview({ settings, docked = false }) {
  const isDark = settings.theme === "dark";
  const bubbleRadius = settings.bubbleStyle === "squircle" ? settings.borderRadius + 10 : settings.borderRadius;
  const pad = settings.spacing === "compact" ? 10 : 16;
  const avatarIcon = launcherIcon(settings);
  const messageSize =
    (settings.fontSize || 15) * (messageFontScale[settings.messageSize] || 1);

  return (
    <div
      className={`cz-widget${isDark ? " is-dark" : ""}${docked ? " is-docked" : ""}`}
      style={{
        borderRadius: docked ? 0 : `${settings.borderRadius}px`,
        fontSize: `${settings.fontSize}px`,
        fontFamily: fontStackFor(settings.fontFamily),
      }}
    >
      <div className="cz-widget-header" style={{ background: settings.headerColor || settings.primaryColor }}>
        {/* An uploaded image stands alone; the tint is only backing for a glyph. */}
        {settings.showAvatar && (
          <span
            className="cz-widget-avatar"
            style={
              settings.avatarImage
                ? undefined
                : { background: "rgba(255,255,255,0.22)" }
            }
          >
            {avatarIcon}
          </span>
        )}
        <div className="cz-widget-heading">
          <strong style={{ fontWeight: nameWeights[settings.nameStyle] || 700 }}>
            {settings.name || "Hostor"}
          </strong>
          {settings.showOnlineStatus && (
            <span className="cz-widget-status">
              <span className="cz-widget-status-dot" />
              Online
            </span>
          )}
        </div>
        <span className="cz-widget-minimize">
          <MinusIcon />
        </span>
      </div>

      <div className="cz-widget-body" style={{ padding: `${pad}px` }}>
        <p
          className="cz-widget-message"
          style={{
            borderRadius: `${bubbleRadius}px`,
            fontSize: `${messageSize}px`,
          }}
        >
          {(settings.welcomeMessage || "").split("\n").map((line, i, lines) => (
            <React.Fragment key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
        {/* {settings.showSourceLinks && <span className="cz-widget-source">Source: yourwebsite.com</span>} */}

        {/* A visitor reply, so the primary colour has somewhere to show. */}
        <p
          className="cz-widget-message cz-widget-message--user"
          style={{
            borderRadius: `${bubbleRadius}px`,
            fontSize: `${messageSize}px`,
            background: settings.primaryColor,
          }}
        >
          What are your opening hours?
        </p>

        {settings.quickActions.length > 0 && (
          <div className="cz-widget-chips">
            {settings.quickActions.map((chip, i) => (
              <button
                type="button"
                key={i}
                style={{
                  borderColor: settings.accentColor,
                  color: settings.buttonColor,
                  borderRadius: `${Math.min(bubbleRadius, 14)}px`,
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="cz-widget-input">
        <input type="text" placeholder="Type your message..." disabled />
        <span className="cz-widget-send" style={{ background: settings.buttonColor }}>
          <SendIcon />
        </span>
      </div>

      {settings.showPoweredBy && (
        <div className="cz-widget-footer">
          Powered by <img src="/assets/logo.svg" alt="Hostor" className="widgetLogo" />
        </div>
      )}
    </div>
  );
}
